import React, { useState, useEffect, useRef } from 'react';
import './Footer.css';
import { useTranslation } from 'react-i18next';
import useLanguagePrefix from '../LanguagePrefix';
import { useLocation, useNavigate } from 'react-router-dom';
import { setLanguage } from '../i18n';

const SUPPORTED = ['en', 'ro', 'es', 'de'];
const API = 'https://api.prostay.com';

function Footer() {
  const langPrefix = useLanguagePrefix();
  const navigate = useNavigate();
  const location = useLocation();
  const [language, setLanguageState] = useState('en');
  const { i18n, t } = useTranslation();

  const [hrefByLang, setHrefByLang] = useState({ en: '#', es: '#', ro: '#', de: '#' });

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Espanol' },
    // { value: 'ro', label: 'Romana' },
    // { value: 'de', label: 'Deutsch' },
  ];

  useEffect(() => {
    const languageMap = { en: 'en', es: 'es', ro: 'ro', de: 'de' };
    setLanguageState(languageMap[i18n.language] || 'en');
  }, [i18n.language]);

  const ensureTrailing = (p) => (p === '/' ? '/' : (p.endsWith('/') ? p : p + '/'));
  const stripLang = (pathname) => {
    const parts = pathname.split('/').filter(Boolean);
    if (parts.length && SUPPORTED.includes(parts[0])) parts.shift();
    const clean = '/' + parts.join('/');
    const hadTrailing = pathname.length > 1 && pathname.endsWith('/');
    return hadTrailing ? ensureTrailing(clean || '/') : (clean || '/');
  };

  const fetchArticleBySlug = async (slug) => {
    try {
      const res = await fetch(`${API}/WebsiteKnowledgebase/get-article-by-slug/${encodeURIComponent(slug)}`);
      if (!res.ok) throw new Error('not ok');
      return await res.json(); // { slug, slugTranslations?: { [lang]: string }, ... }
    } catch (e) {
      console.error('slug fetch failed', e);
      return null;
    }
  };

  // ⭐ Correct slug resolver (fix): English uses article.slug (default)
  const getTargetSlug = (lang, articleData, currentSlug) => {
    if (!articleData) return currentSlug;
    if (lang === 'en') {
      return articleData.slug || articleData.slugTranslations?.en || currentSlug;
    }
    return articleData.slugTranslations?.[lang] || currentSlug;
  };

  const buildHrefFor = (lang, basePath, qs, hash, articleData, context) => {
    let path = basePath;

    if (context?.type === 'blog') {
      const targetSlug = getTargetSlug(lang, articleData, context.currSlug);
      path = `/blog/${targetSlug}${context.pageSeg || ''}/`;
    } else if (context?.type === 'resources') {
      const targetSlug = getTargetSlug(lang, articleData, context.currSlug);
      path = `/resources/${context.cat}/${context.subcat}/${targetSlug}/`;
    } else {
      path = ensureTrailing(basePath);
    }

    const localized = lang === 'en' ? path : `/${lang}${path}`;
    return `${localized}${qs}${hash}`;
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const base = stripLang(location.pathname);
      const qs = location.search || '';
      const hash = location.hash || '';

      let context = { type: null };
      const blogMatch = base.match(/^\/blog\/([^/]+)(?:\/page\/(\d+))?\/?$/i);
      const resMatch = base.match(/^\/resources\/([^/]+)\/([^/]+)\/([^/]+)\/?$/i);

      let articleData = null;

      if (blogMatch) {
        context = { type: 'blog', currSlug: blogMatch[1], pageSeg: blogMatch[2] ? `/page/${blogMatch[2]}` : '' };
        articleData = await fetchArticleBySlug(context.currSlug);
      } else if (resMatch) {
        context = { type: 'resources', cat: resMatch[1], subcat: resMatch[2], currSlug: resMatch[3] };
        articleData = await fetchArticleBySlug(context.currSlug);
      }

      const next = {};
      SUPPORTED.forEach((lang) => {
        next[lang] = buildHrefFor(lang, base, qs, hash, articleData, context);
      });

      if (!cancelled) setHrefByLang(next);
    })();

    return () => { cancelled = true; };
  }, [location.pathname, location.search, location.hash]);

  const handleLanguageChange = async (lang, e) => {
    if (e && e.preventDefault) e.preventDefault();
    try {
      await setLanguage(lang);
    } finally {
      const dest = hrefByLang[lang] || '#';
      navigate(dest, { replace: true });
      handleClose();
    }
  };

  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const handleToggle = () => setIsOpen((prev) => !prev);
  const handleClose = () => setIsOpen(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        handleClose();
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className='footer'>
      <div className='d-flex justify-content-center'>
        <div className='footer-grid page-content'>
          <div className='col'>
            <img src="/images/prostay-logo-dark.svg" alt="Prostay Logo" className='logo' />
            <p className='body-large' style={{ marginBottom: '28px' }}>
              {t('Your Vision, Our Solution.')}
            </p>

            <div ref={menuRef} style={{ position: 'relative', display: 'inline-block' }}>
              <button className='lang-picker-btn' onClick={handleToggle} style={{ padding: '0.5em 1em' }}>
                {t(languageOptions.find((opt) => opt.value === language)?.label)}
                <svg width="12" height="8" viewBox="0 0 14 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1.5L7 7.5L13 1.5" stroke="#808698" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              <ul className={`lang-menu ${isOpen ? 'visible' : ''}`} aria-hidden={!isOpen}>
                {languageOptions.map((type) => (
                  <li key={type.value}>
                    <a
                      href={hrefByLang[type.value] || '#'}
                      onClick={(e) => handleLanguageChange(type.value, e)}
                    >
                      {t(type.label)}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* your other footer columns unchanged */}
          <div className='d-flex flex-column'>
            <small>{t('Products')}</small>
            <a href={`${langPrefix}/ai/`}><label>{t('Prostay AI')}</label></a>
            <a href={`${langPrefix}/property-management-system/`}><label>{t('Property Management')}</label></a>
            <a href={`${langPrefix}/channel-manager/`}><label>{t('Channel Manager')}</label></a>
            <a href={`${langPrefix}/booking-engine/`}><label>{t('Booking Engine')}</label></a>
            <a href={`${langPrefix}/prostay-nexus/`}><label>{t('Prostay Nexus')}</label></a>
            <a href={`${langPrefix}/point-of-sale/`}><label>{t('Point of Sale')}</label></a>
            <a href={`${langPrefix}/prostay-pay/`}><label>{t('Prostay Pay')}</label></a>
          </div>

          <div className='d-flex flex-column'>
            <small>{t('Features')}</small>
            <a href={`${langPrefix}/ai/ai-booking-assistant/`}><label>{t('AI Booking Assistant')}</label></a>
            <a href={`${langPrefix}/ai/ai-front-desk-agent/`}><label>{t('AI Front Desk Agent')}</label></a>
            <a href={`${langPrefix}/hotel-sales-software/`}><label>{t('Hotel Sales Software')}</label></a>
          </div>

          <div className='d-flex flex-column'>
            <small>{t('Solutions')}</small>
            <a href={`${langPrefix}/hotel-management-software/`}><label>{t('Hotels')}</label></a>
            <a href={`${langPrefix}/multi-property-groups/`}><label>{t('Multi-Property Groups')}</label></a>
            <a href={`${langPrefix}/vacation-rentals/`}><label>{t('Vacation Rentals')}</label></a>
            <a href={`${langPrefix}/bed-breakfast/`}><label>{t('Bed & Breakfasts')}</label></a>
            <a href={`${langPrefix}/small-hotels/`}><label>{t('Small Hotels')}</label></a>
          </div>

          <div className='d-flex flex-column'>
            <small>{t('Company')}</small>
            <a href={`${langPrefix}/contact-us/`}><label>{t('Contact Us')}</label></a>
            <a href={`${langPrefix}/news/`}><label>{t('News & Press')}</label></a>
            <a href={`${langPrefix}/blog/`}><label>{t('Blog')}</label></a>
            <a href={`${langPrefix}/reviews/`}><label>{t('Reviews')}</label></a>
            <a href={`${langPrefix}/compare/`}><label>{t('Compare')}</label></a>
          </div>

          <div className='d-flex flex-column'>
            <small>{t('Resources')}</small>
            <a href={`${langPrefix}/resources/`}><label>{t('All Resources')}</label></a>
            <a href={`${langPrefix}/product-updates/`}><label>{t('Product Updates')}</label></a>
            <a href={`${langPrefix}/tools/`}><label>{t('Tools')}</label></a>
          </div>
        </div>
      </div>

      <div className='d-flex justify-content-center mb-5'>
        <div className='d-flex page-content gap-2 gap-lg-5'>
          <a href={`${langPrefix}/terms-and-conditions/`}><label>{t('Terms of Service')}</label></a>
          <a href={`${langPrefix}/privacy-policy/`}><label>{t('Policy')}</label></a>
          <a href={`${langPrefix}/cookies-policy/`}><label>{t('Cookies')}</label></a>
          <a href={`${langPrefix}/data-privacy/`}><label>{t('Data Security')}</label></a>
          <a href={`${langPrefix}/privacy-policy/`}><label>{t('Do Not Sell or Share My Personal Information')}</label></a>
        </div>
      </div>

      <div className='footer-bottom d-flex justify-content-center'>
        <div className='page-content d-flex justify-content-between'>
          <small style={{ fontWeight: '400' }}>© 2025 prostay. {t('All rights reserved.')}</small>
          <div className='d-flex justify-content-center align-items-center gap-4'>
            <a href="https://x.com/ProstayPMS" target="_blank" rel="noopener noreferrer">
              <svg width="24" height="22" viewBox="0 0 24 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M15.9455 22L10.396 14.0901L3.44886 22H0.509766L9.09209 12.2311L0.509766 0H8.05571L13.286 7.45502L19.8393 0H22.7784L14.5943 9.31648L23.4914 22H15.9455ZM19.2185 19.77H17.2398L4.71811 2.23H6.6971L11.7121 9.25316L12.5793 10.4719L19.2185 19.77Z" fill="#B0B4BF" />
              </svg>
            </a>
            <a href="https://linkedin.com/company/prostaypms" target="_blank" rel="noopener noreferrer">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.2234 0H1.77187C0.792187 0 0 0.773438 0 1.72969V22.2656C0 23.2219 0.792187 24 1.77187 24H22.2234C23.2031 24 24 23.2219 24 22.2703V1.72969C24 0.773438 23.2031 0 22.2234 0ZM7.12031 20.4516H3.55781V8.99531H7.12031V20.4516ZM5.33906 7.43438C4.19531 7.43438 3.27188 6.51094 3.27188 5.37187C3.27188 4.23281 4.19531 3.30937 5.33906 3.30937C6.47813 3.30937 7.40156 4.23281 7.40156 5.37187C7.40156 6.50625 6.47813 7.43438 5.33906 7.43438ZM20.4516 20.4516H16.8937V14.8828C16.8937 13.5562 16.8703 11.8453 15.0422 11.8453C13.1906 11.8453 12.9094 13.2937 12.9094 14.7891V20.4516H9.35625V8.99531H12.7687V10.5609H12.8156C13.2891 9.66094 14.4516 8.70938 16.1813 8.70938C19.7859 8.70938 20.4516 11.0813 20.4516 14.1656V20.4516Z" fill="#B0B4BF" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Footer;
